import { useEffect } from 'react';
import * as React from 'react';

import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Grid, IconButton, Slider, useMediaQuery,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { TrackingAction, TrackingCategory, trackingService } from '../../services';
import { toMMSS } from '../../utils';
import { type ITheme } from '../../theme';

const useStyles = makeStyles()(() => ({
  time: {
    fontSize: '12px',
    color: 'rgba(51,51,51,.7)',
    marginTop: 16,
  },
}));

interface AudioPlayerProps {
  src: ArrayBuffer | string;
  maxDuration?: number;
  endCallback?: () => void;
  playCallback?: (time: number) => void;
  className?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  maxDuration,
  endCallback,
  className,
  playCallback,
}) => {
  const { classes } = useStyles();
  const [time, setTime] = React.useState(0);
  const [duration, setDuration] = React.useState(60);
  const [playing, setPlaying] = React.useState(false);
  const [audio, setAudio] = React.useState(null);
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('xs'));

  useEffect(() => {
    let aud: HTMLAudioElement = null;
    if (src instanceof ArrayBuffer) {
      const blob = new Blob([src], { type: 'audio/mp3' });
      const url = window.URL.createObjectURL(blob);
      aud = new Audio(url);
    } else {
      aud = new Audio(src);
    }
    aud.oncanplaythrough = () => {
      setDuration(maxDuration ? Math.min(maxDuration, aud.duration) : aud?.duration);
    };
    aud.ontimeupdate = () => {
      // stop playback if demo duration is exceeded
      if (maxDuration && aud?.currentTime > maxDuration) {
        aud?.pause();
        setPlaying(false);
        if (endCallback instanceof Function) {
          endCallback();
        }
      } else if (playCallback instanceof Function) {
        playCallback(aud?.currentTime);
      }

      setAudio(aud);
      setTime(aud?.currentTime);
    };
    setAudio(aud);
  }, []);

  const playPauseClick = () => {
    if (playing) {
      audio?.pause();
      setPlaying(false);
    } else {
      audio?.play();
      setPlaying(true);
    }

    if (
      src !== '/demo/example_original.mp3'
      && src !== '/demo/example_midi.mp3'
      && typeof src === 'string'
    ) {
      trackingService.event(
        TrackingCategory.Free,
        src.includes('original')
          ? TrackingAction.PLAY_DEMO_ORIGINAL
          : TrackingAction.PLAY_DEMO_SYNTHESIZED,
      );
    }
  };

  const seekTime = (_event: any, newValue: number) => {
    audio.currentTime = newValue;
    setAudio(audio);
    setTime(newValue);
  };

  return (
    <div className={className}>
      <Grid container spacing={2} style={{ paddingRight: 12 }}>
        <Grid item>
          <IconButton aria-label="play/pause" onClick={playPauseClick} size="large">
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Grid>
        <Grid item className={classes.time} style={{ marginLeft: -18 }}>
          {toMMSS(time)}
        </Grid>
        <Grid item xs style={{ paddingTop: isMobile ? 10 : 18 }}>
          <Slider aria-label="time track" value={time} onChange={seekTime} max={duration} />
        </Grid>
        <Grid item className={classes.time}>
          {toMMSS(duration)}
        </Grid>
      </Grid>
    </div>
  );
};

export default AudioPlayer;
